import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import PageIntro from '@/components/feature/PageIntro';
import Reveal from '@/components/base/Reveal';
import { serviceCatalog, servicesIntroImage } from '@/mocks/site';
import useDocumentMeta from '@/hooks/useDocumentMeta';

export default function ServicesPage() {
  useDocumentMeta('Auto Repair & Maintenance Services | Redline Garage', 'Engine diagnostics, routine maintenance, brakes and suspension, tires and alignment, electrical systems and air conditioning repair at Redline Garage.');

  return (
    <SiteLayout>
      <PageIntro
        title="Services"
        text="From scheduled maintenance to complex fault-finding — every service follows the same method: diagnose accurately, explain clearly, repair properly."
        image={servicesIntroImage}
        alt="Red sports car wheel inside auto repair workshop"
      />
      <section className="py-16 md:py-[104px]">
        <div className="mx-auto max-w-[1280px] px-4 md:px-8 lg:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-5 md:gap-x-6 md:gap-y-8">
            {serviceCatalog.map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 90} className="h-full">
                <article className="group relative h-full flex flex-col bg-background-100/60 border border-background-300/40 hover:border-background-300/90 transition-colors duration-500">
                  <span aria-hidden="true" className="absolute top-0 left-0 h-px w-10 bg-primary-500 z-10 transition-all duration-500 group-hover:w-20" />
                  <div className="relative h-[210px] md:h-[230px] overflow-hidden bg-background-200">
                    <img src={s.image} alt={`${s.title} service`} title={`${s.title} — Redline Garage`} className="w-full h-full object-cover object-center transition-transform duration-[280ms] ease-out group-hover:scale-[1.025]" />
                    <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background-100/70 to-transparent" />
                  </div>
                  <div className="px-6 pt-6 pb-5 md:px-7 md:pt-7 flex flex-col flex-1 border-t border-background-300/40">
                    <div className="flex items-center gap-3">
                      <span className="font-heading text-[11px] tracking-[0.28em] text-primary-500">0{i + 1}</span>
                      <span aria-hidden="true" className="h-px flex-1 bg-background-300/40" />
                    </div>
                    <h2 className="mt-4 font-heading uppercase text-[22px] md:text-[24px] leading-[1.1] tracking-[0.01em] text-foreground-950">{s.title}</h2>
                    <p className="mt-3 text-[13.5px] md:text-sm text-foreground-600 leading-[1.7]">{s.text}</p>
                    <div className="mt-auto pt-7">
                      <Link to={`/contact?service=${s.id}`} className="inline-flex items-center gap-2.5 min-h-[44px] text-[11px] font-semibold uppercase tracking-[0.22em] text-foreground-900 hover:text-foreground-950 whitespace-nowrap cursor-pointer transition-colors">
                        <span className="border-b border-background-300/70 group-hover:border-primary-500 pb-1 transition-colors duration-300">Book this service</span>
                        <i className="ri-arrow-right-up-line text-sm text-primary-500 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
                      </Link>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="pb-16 md:pb-[104px]">
        <div className="mx-auto max-w-[1280px] px-4 md:px-8 lg:px-10">
          <div className="relative border-y border-background-300/50 bg-background-100/40 px-6 py-10 md:px-12 md:py-12 flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <h2 className="font-heading uppercase text-[32px] md:text-[40px] leading-none">Not sure what your car needs?</h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-xl leading-relaxed">Describe the symptoms and we&apos;ll start with an inspection — then recommend only what&apos;s necessary.</p>
            </div>
            <Link to="/contact?service=inspection" className="group/cta self-start md:self-center inline-flex items-center gap-4 min-h-[52px] px-6 border border-background-300/70 hover:border-primary-500/70 text-xs font-semibold uppercase tracking-[0.24em] text-foreground-950 whitespace-nowrap cursor-pointer transition-colors duration-300">
              Book an Inspection
              <i className="ri-arrow-right-up-line text-base text-primary-500 transition-transform duration-200 group-hover/cta:translate-x-1 group-hover/cta:-translate-y-1" />
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}